import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastModule } from 'primeng/toast';

export interface SocialMediaLinks {
  twitter?: string;
  facebook?: string;
  instagram?: string;
  discord?: string;
  telegram?: string;
  linkedin?: string;
}

@Component({
  selector: 'app-community',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    MessageModule,
    CardModule,
    SkeletonModule,
    ToastModule
  ],
  providers: [MessageService],
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.css']
})
export class CommunityComponent implements OnInit, OnDestroy {
  // Loading states
  loading = signal(true);
  submitting = signal(false);
  
  private timeout?: number;
  private submitTimeout?: number;
  // Form controls
  twitterControl = new FormControl('', [Validators.required, this.urlValidator]);
  facebookControl = new FormControl('', [Validators.required, this.urlValidator]);
  instagramControl = new FormControl('', [Validators.required, this.urlValidator]);
  discordControl = new FormControl('', [Validators.required, this.urlValidator]);
  telegramControl = new FormControl('', [Validators.required, this.urlValidator]);
  linkedinControl = new FormControl('', [Validators.required, this.urlValidator]);

  communityForm: FormGroup;

  constructor(
    private translateService: TranslateService,
    private messageService: MessageService
  ) {
    this.communityForm = new FormGroup({
      twitter: this.twitterControl,
      facebook: this.facebookControl,
      instagram: this.instagramControl,
      discord: this.discordControl,
      telegram: this.telegramControl,
      linkedin: this.linkedinControl
    });
  }

  ngOnInit(): void {
    this.loadSocialMediaData();
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }
  }

  private loadSocialMediaData(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    
    this.timeout = window.setTimeout(() => {
      // Load existing social media links (would typically come from a service)
      const savedData: SocialMediaLinks = {
        twitter: 'https://twitter.com/example',
        facebook: 'https://facebook.com/example',
        instagram: 'https://instagram.com/example',
        discord: 'https://discord.gg/example',
        telegram: 'https://t.me/example',
        linkedin: 'https://linkedin.com/company/example'
      };

      // Populate form with existing data
      this.twitterControl.setValue(savedData.twitter || '');
      this.facebookControl.setValue(savedData.facebook || '');
      this.instagramControl.setValue(savedData.instagram || '');
      this.discordControl.setValue(savedData.discord || '');
      this.telegramControl.setValue(savedData.telegram || '');
      this.linkedinControl.setValue(savedData.linkedin || '');
      
      this.loading.set(false);
    }, 2000);
  }

  onSubmit(): void {
    if (this.communityForm.valid && !this.submitting()) {
      this.submitting.set(true);
      
      const socialMediaData: SocialMediaLinks = {
        twitter: this.twitterControl.value!,
        facebook: this.facebookControl.value!,
        instagram: this.instagramControl.value!,
        discord: this.discordControl.value!,
        telegram: this.telegramControl.value!,
        linkedin: this.linkedinControl.value!
      };

      // Simulate save operation with loading state
      this.submitTimeout = window.setTimeout(() => {
        // Save the data (would typically call a service)
        this.saveSocialMediaLinks(socialMediaData);
        
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('community.success.title'),
          detail: this.translateService.instant('community.success.message'),
          life: 4000
        });
        
        this.submitting.set(false);
      }, 2000); // 2 second loading simulation
    } else if (!this.communityForm.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.communityForm.controls).forEach(key => {
        this.communityForm.get(key)?.markAsTouched();
      });
    }
  }

  private saveSocialMediaLinks(data: SocialMediaLinks): void {
    // TODO: Implement actual save logic here
    console.log('Saving social media links:', data);
  }

  // URL validator
  urlValidator(control: any) {
    if (!control.value) return null;
    const urlPattern = /^https?:\/\/.+\..+/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  // Error message methods
  getTwitterErrorMessage(): string {
    if (this.twitterControl.errors?.['required']) {
      return this.translateService.instant('community.errors.twitterRequired');
    }
    if (this.twitterControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.twitterInvalid');
    }
    return '';
  }

  getFacebookErrorMessage(): string {
    if (this.facebookControl.errors?.['required']) {
      return this.translateService.instant('community.errors.facebookRequired');
    }
    if (this.facebookControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.facebookInvalid');
    }
    return '';
  }

  getInstagramErrorMessage(): string {
    if (this.instagramControl.errors?.['required']) {
      return this.translateService.instant('community.errors.instagramRequired');
    }
    if (this.instagramControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.instagramInvalid');
    }
    return '';
  }

  getDiscordErrorMessage(): string {
    if (this.discordControl.errors?.['required']) {
      return this.translateService.instant('community.errors.discordRequired');
    }
    if (this.discordControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.discordInvalid');
    }
    return '';
  }

  getTelegramErrorMessage(): string {
    if (this.telegramControl.errors?.['required']) {
      return this.translateService.instant('community.errors.telegramRequired');
    }
    if (this.telegramControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.telegramInvalid');
    }
    return '';
  }

  getLinkedinErrorMessage(): string {
    if (this.linkedinControl.errors?.['required']) {
      return this.translateService.instant('community.errors.linkedinRequired');
    }
    if (this.linkedinControl.errors?.['invalidUrl']) {
      return this.translateService.instant('community.errors.linkedinInvalid');
    }
    return '';
  }
}